import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Heart, User, Search, Menu, X, ShieldCheck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Badge } from "@/components/ui/badge";

const links = [
  { to: "/shop", label: "Explore Collection" },
  { to: "/shop?collection=Festive Signature", label: "Festive" },
  { to: "/customisation", label: "Customisation" },
  { to: "/gifting", label: "Gifting & Corporate" },
  { to: "/catalogue", label: "Catalogue" },
];

export const Navbar = () => {
  const { cartCount, wishlist, user, isAdmin, setAuthOpen } = useStore();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/shop?search=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <div className="bg-[#120A07] text-center py-2 text-[11px] tracking-[0.25em] uppercase text-[#E6C594] font-body border-b border-[#3B251D]">
        Complimentary delivery on orders ₹3,500 & above
      </div>
      <header
        data-testid="navbar"
        className={`sticky top-0 z-40 transition-all duration-500 ${scrolled ? "glass border-b border-[#3B251D]" : "bg-transparent"}`}
      >
        <nav className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 flex items-center justify-between gap-6 h-20">
          <button className="lg:hidden text-[#FDFBF7]" onClick={() => setOpen(true)} data-testid="mobile-menu-open">
            <Menu size={24} />
          </button>

          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 sm:gap-3 leading-none group">
            <img src="/brand/emblem.png" alt="SCHOKODALE" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40 transition-transform duration-500 group-hover:rotate-[8deg]" />
            <span className="flex flex-col">
              <span className="font-display text-xl sm:text-2xl font-extrabold tracking-[0.16em] lg:tracking-[0.1em] 2xl:tracking-[0.16em] gold-gradient-text">SCHOKODALE</span>
              <span className="hidden sm:block text-[9px] tracking-[0.4em] text-[#8C7B6E] mt-0.5">THE VALLEY OF CHOCOLATES</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-7 font-body text-sm whitespace-nowrap">
            {links.map((l) => (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.split(" ")[0].toLowerCase()}`}
                className="text-[#C8B8AB] hover:text-[#D4AF37] transition-colors tracking-wide">
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 sm:gap-5">
            <form onSubmit={submitSearch} className="hidden md:flex lg:hidden 2xl:flex items-center bg-[#241611] border border-[#3B251D] rounded-full px-3 py-1.5">
              <Search size={15} className="text-[#8C7B6E]" />
              <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search chocolates"
                className="bg-transparent outline-none text-sm text-[#FDFBF7] placeholder:text-[#8C7B6E] w-28 lg:w-40 ml-2" />
            </form>

            <Link to="/shop" aria-label="Search" data-testid="nav-search-icon" className="hidden lg:block 2xl:hidden text-[#FDFBF7] hover:text-[#D4AF37] transition-colors"><Search size={20} /></Link>

            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1 text-[#D4AF37] text-xs border border-[#D4AF37]/40 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10 transition">
                <ShieldCheck size={14} /> <span className="lg:hidden 2xl:inline">Admin</span>
              </Link>
            )}

            <Link to="/account?tab=wishlist" data-testid="nav-wishlist" className="relative text-[#FDFBF7] hover:text-[#D4AF37] transition-colors">
              <Heart size={21} />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#1A0F0B] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{wishlist.length}</span>}
            </Link>

            <button data-testid="nav-account" onClick={() => user ? nav("/account") : setAuthOpen(true)} className="text-[#FDFBF7] hover:text-[#D4AF37] transition-colors">
              <User size={21} />
            </button>

            <Link to="/cart" data-testid="nav-cart" className="relative text-[#FDFBF7] hover:text-[#D4AF37] transition-colors">
              <ShoppingBag size={21} />
              {cartCount > 0 && <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#1A0F0B] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{cartCount}</span>}
            </Link>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-[#1A0F0B] border-r border-[#3B251D] p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-8">
              <span className="font-display text-xl gold-gradient-text">SCHOKODALE</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X className="text-[#FDFBF7]" /></button>
            </div>
            <form onSubmit={submitSearch} className="flex items-center bg-[#241611] border border-[#3B251D] rounded-full px-3 py-2 mb-6">
              <Search size={16} className="text-[#8C7B6E]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-transparent outline-none text-sm text-[#FDFBF7] ml-2 w-full" />
            </form>
            <div className="flex flex-col gap-5 font-body">
              {links.map((l) => (
                <Link key={l.label} to={l.to} className="text-[#C8B8AB] hover:text-[#D4AF37] text-lg">{l.label}</Link>
              ))}
              {isAdmin && <Link to="/admin" className="text-[#D4AF37] text-lg">Admin Panel</Link>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
